// utils/currency/parse.js
//
// The tolerant reader of user input for a currency element: numbers (major
// units: 12.5 is twelve dollars fifty), `{ amount, currency }` objects (the
// stored shape: amount in MINOR units), `{ value, currency }` objects (major
// units, the friendlier machine spelling) and free-text strings ("$12.50",
// "12.5 CAD", "USD 1,234.50", "CA$ 12", "-€3", "1,234.5"). Understands,
// never enforces: the element's allowlist and bounds are the normaliser's job
// (normalize.js). Never throws. Dependency-free and vendored into keepr-web
// (see registry.js for the rule).
//
// MAJOR -> MINOR IS DONE ON DECIMAL TEXT, NEVER BY MULTIPLYING A FLOAT.
// 19.99 * 100 is 1998.9999999999998 in binary floating point; reading the
// shortest round-trip spelling of the number ("19.99") and moving its
// decimal point is exact for every amount a person can type. A number whose
// spelling needs an exponent ("1e-7") is expanded first.
//
// PRECISION. `options.fraction` says what a digit beyond the currency's
// exponent means ("1.234 USD"):
//   'refuse' (default)  a `type` error -- the write path's rule: an entry
//                       more precise than the currency is a mistake, and
//                       silently storing $1.23 for "1.234" would be the
//                       platform deciding what somebody meant.
//   'round'             half away from zero -- the READ path's rule for a
//                       stray bare number, and the number->currency
//                       conversion's rule (docs/SCHEMA.md).
//   'keep'              a FRACTIONAL minor-unit amount -- KQL range bounds
//                       (`price > 20.555` is a perfectly good question).
//
// A decimal comma is not guessed: "1,234.50" drops its grouping comma, "12,50"
// is refused rather than read as 1250 (the rule utils/units/parse.js keeps).

'use strict';

const { MAX_AMOUNT, getCurrency, currencyCandidates, pickCandidate, resolveCurrency, defaultCodeOf } = require('./registry');

const FRACTION_MODES = ['refuse', 'round', 'keep'];

// Only a thousands separator is dropped ("1,234.5"); a comma anywhere else
// ("1,5" as a decimal, "1,2,3") is left in place to fail.
const GROUPING_COMMA_RE = /,(?=\d{3}(?!\d))/g;
const DECIMAL_TEXT_RE = /^([+-]?)(\d*)(?:\.(\d*))?$/;
const INTEGER_TEXT_RE = /^[+-]?\d+$/;
// [sign] [prefix spelling] [sign] number [suffix spelling]. A spelling is
// anything that is not a digit, a sign or (as its first character) a point;
// the lazy prefix lets "$.50" read as "$" + ".50".
const STRING_RE = /^([+-]?)\s*([^\d\s.+-][^\d+-]*?)?\s*([+-]?)\s*(\d+\.?\d*|\.\d+)\s*([^\d\s.+-][^\d+-]*?)?$/;

function reject(code, message) {
	return { error: code, message };
}

function modeOf(options) {
	const mode = options && options.fraction;
	return FRACTION_MODES.indexOf(mode) === -1 ? 'refuse' : mode;
}

/**
 * A finite number as plain decimal text, exponent expanded: 19.99 -> "19.99",
 * 1e-7 -> "0.0000001", 1.5e21 -> "1500000000000000000000". Null when not
 * finite.
 */
function decimalTextOf(n) {
	if (typeof n !== 'number' || !Number.isFinite(n)) return null;
	const text = String(n);
	if (text.indexOf('e') === -1) return text;
	const m = text.match(/^(-?)(\d+)(?:\.(\d+))?e([+-]\d+)$/);
	if (!m) return null;
	const digits = m[2] + (m[3] || '');
	const point = m[2].length + Number(m[4]);
	let out;
	if (point <= 0) out = '0.' + '0'.repeat(-point) + digits;
	else if (point >= digits.length) out = digits + '0'.repeat(point - digits.length);
	else out = digits.slice(0, point) + '.' + digits.slice(point);
	return m[1] + out;
}

/**
 * Plain decimal text in MAJOR units -> the amount in minor units, at
 * `exponent`, under `mode` (see the header). { amount } | { error, message }.
 */
function minorFromText(text, exponent, mode, code) {
	const m = typeof text === 'string' ? text.match(DECIMAL_TEXT_RE) : null;
	if (!m || !/\d/.test(m[2] + (m[3] || ''))) return reject('type', '"' + text + '" is not an amount');
	const sign = m[1] === '-' ? -1 : 1;
	const whole = m[2] || '0';
	const fraction = m[3] || '';
	const kept = (fraction.slice(0, exponent) + '0'.repeat(exponent)).slice(0, exponent);
	const rest = fraction.slice(exponent);
	let amount = Number((whole + kept).replace(/^0+(?=\d)/, ''));
	if (/[1-9]/.test(rest)) {
		if (mode === 'refuse') {
			return reject('type', '"' + text.replace(/^\+/, '') + '" has more decimal places than ' + code + ' allows ('
				+ exponent + ')');
		}
		if (mode === 'round') {
			if (rest.charAt(0) >= '5') amount += 1;
		} else {
			amount = Number((whole + kept).replace(/^0+(?=\d)/, '') + '.' + rest);
		}
	}
	if (!Number.isFinite(amount) || amount > MAX_AMOUNT) {
		return reject('range', '"' + text.replace(/^\+/, '') + '" is too large an amount');
	}
	return { amount: amount === 0 ? 0 : sign * amount };
}

/**
 * major (a number, or decimal text) -> minor units in `code`, under `mode`;
 * null when the code is unknown, the input is not a decimal, or the mode
 * refuses it. toMinor(12.5, 'USD') === 1250; toMinor(12.345, 'USD') === null;
 * toMinor(12.345, 'USD', 'round') === 1235; toMinor(12.345, 'USD', 'keep') === 1234.5.
 */
function toMinor(major, code, mode) {
	const currency = getCurrency(code);
	if (!currency) return null;
	const text = typeof major === 'number' ? decimalTextOf(major) : (typeof major === 'string' ? major.trim() : null);
	if (text === null) return null;
	const result = minorFromText(text, currency.exponent, FRACTION_MODES.indexOf(mode) === -1 ? 'refuse' : mode, currency.code);
	return result.error ? null : result.amount;
}

function describeElement(options) {
	return defaultCodeOf(options) ? '' : ' (this element names no currency)';
}

function codeFor(spelling, options, raw) {
	const code = resolveCurrency(spelling, options);
	if (!code) return reject('invalid_currency', '"' + String(spelling).trim() + '" is not a currency' + (raw !== undefined ? ' in "' + raw + '"' : ''));
	return { code };
}

function defaultCode(options) {
	const code = defaultCodeOf(options);
	if (!code) return reject('invalid_currency', 'an amount needs a currency' + describeElement(options));
	return { code };
}

function parseString(raw, options, mode) {
	const text = raw.replace(GROUPING_COMMA_RE, '').trim();
	if (!text) return reject('type', 'an amount is required');
	if (text.indexOf(',') !== -1) return reject('type', '"' + raw + '" is not a valid amount');
	const m = text.match(STRING_RE);
	if (!m) return reject('type', '"' + raw + '" is not a valid amount');
	const [, outerSign, prefix, innerSign, digits, suffix] = m;
	if (outerSign && innerSign) return reject('type', '"' + raw + '" is not a valid amount');

	// "$12 USD" is one currency spelled twice; "$12 CAD" on a USD+CAD element
	// is too (CAD prints "$" as well), and "€12 USD" is two. Each spelling
	// narrows the candidates, and the element picks among what is left.
	let candidates = null;
	let glyph = '';
	for (const spelling of [prefix, suffix]) {
		if (!spelling || !spelling.trim()) continue;
		const found = currencyCandidates(spelling);
		if (!found.length) return reject('invalid_currency', '"' + spelling.trim() + '" is not a currency in "' + raw + '"');
		if (found.length > 1) glyph = spelling.trim().replace(/\s+/g, ' ');
		candidates = candidates ? candidates.filter(code => found.indexOf(code) !== -1) : found;
		if (!candidates.length) return reject('invalid_currency', '"' + raw + '" names two different currencies');
	}
	let code = candidates ? pickCandidate(candidates, options, glyph) : null;
	if (candidates && !code) {
		return reject('invalid_currency', '"' + glyph + '" could be any of ' + candidates.join(', ') + ' -- name the currency, e.g. "' + digits + ' ' + candidates[0] + '"');
	}
	if (!code) {
		const fallback = defaultCode(options);
		if (fallback.error) return fallback;
		code = fallback.code;
	}
	const sign = outerSign || innerSign;
	const minor = minorFromText((sign === '-' ? '-' : '') + digits, getCurrency(code).exponent, mode, code);
	if (minor.error) return minor;
	return { amount: minor.amount, currency: code };
}

function parseObject(raw, options, mode) {
	const hasAmount = raw.amount !== undefined && raw.amount !== null && raw.amount !== '';
	const hasValue = raw.value !== undefined && raw.value !== null && raw.value !== '';
	if (hasAmount && hasValue) return reject('type', 'send `amount` (minor units) or `value` (major units), not both');
	if (!hasAmount && !hasValue) return reject('type', 'an amount needs `amount` (minor units) or `value` (major units)');

	let code;
	if (raw.currency === undefined || raw.currency === null || raw.currency === '') {
		const fallback = defaultCode(options);
		if (fallback.error) return fallback;
		code = fallback.code;
	} else {
		if (typeof raw.currency !== 'string') return reject('invalid_currency', 'a currency must be an ISO 4217 code like "USD"');
		const found = codeFor(raw.currency, options);
		if (found.error) return found;
		code = found.code;
	}

	if (hasAmount) {
		let amount = raw.amount;
		if (typeof amount === 'string' && INTEGER_TEXT_RE.test(amount.trim())) amount = Number(amount.trim());
		if (typeof amount !== 'number' || !Number.isFinite(amount) || !Number.isInteger(amount)) {
			return reject('type', '`amount` is a whole number of minor units (1250 is 12.50 ' + code + ')');
		}
		if (Math.abs(amount) > MAX_AMOUNT) return reject('range', '`amount` is too large');
		return { amount: amount === 0 ? 0 : amount, currency: code };
	}

	const value = raw.value;
	let text = null;
	if (typeof value === 'number') text = decimalTextOf(value);
	else if (typeof value === 'string' && DECIMAL_TEXT_RE.test(value.trim())) text = value.trim();
	if (text === null) return reject('type', '`value` must be a number of ' + code);
	const minor = minorFromText(text, getCurrency(code).exponent, mode, code);
	if (minor.error) return minor;
	return { amount: minor.amount, currency: code };
}

/**
 * Like parseCurrency but says WHY it failed:
 *   { amount, currency } | { error: 'type' | 'invalid_currency' | 'range', message }
 * `options`: the element options ({ currencies, defaultCurrency }) plus
 * `fraction` ('refuse' | 'round' | 'keep').
 */
function parseCurrencyDetailed(input, options) {
	const opts = options || {};
	const mode = modeOf(opts);
	if (input === undefined || input === null) return reject('type', 'an amount is required');
	if (typeof input === 'number') {
		if (!Number.isFinite(input)) return reject('type', 'an amount must be a finite number');
		const fallback = defaultCode(opts);
		if (fallback.error) return fallback;
		const minor = minorFromText(decimalTextOf(input), getCurrency(fallback.code).exponent, mode, fallback.code);
		if (minor.error) return minor;
		return { amount: minor.amount, currency: fallback.code };
	}
	if (typeof input === 'string') return parseString(input, opts, mode);
	if (typeof input === 'object' && !Array.isArray(input)) return parseObject(input, opts, mode);
	return reject('type', 'an amount must be a number, a string or an { amount, currency } object');
}

/**
 * parseCurrency(input, options) -> { amount, currency } | null. Never throws.
 * "$12.50" -> { amount: 1250, currency: 'USD' } (for an element whose "$" is USD).
 */
function parseCurrency(input, options) {
	try {
		const result = parseCurrencyDetailed(input, options);
		return result && result.error ? null : result;
	} catch (e) {
		return null;
	}
}

module.exports = {
	decimalTextOf,
	toMinor,
	parseCurrency,
	parseCurrencyDetailed,
};
